import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, WEB_URL } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ArrowLeft, Trash2, Users, Flag, Search, Shield, RefreshCw, ShieldCheck, ShieldOff, Store, Plus, Copy, Check, X, Eye, EyeOff, ChevronDown, ChevronUp, MapPin, ExternalLink, Building2, Megaphone, Send, Loader2, Bell, BellOff, BarChart3, Award, Star, Zap, Trophy, Key, Mail, FileText, Tag, Percent, Gift, PartyPopper, ToggleLeft, ToggleRight, Edit, Phone, Globe } from "lucide-react";
import { toast } from "sonner";

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [editingNotes, setEditingNotes] = useState({});
  const [savingNotes, setSavingNotes] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  
  // User filters
  const [roleFilter, setRoleFilter] = useState("all"); // all, bartender, bargoer
  const [followerFilter, setFollowerFilter] = useState("all"); // all, 0-24, 25-99, 100-499, 500+
  const [sortBy, setSortBy] = useState("newest"); // newest, followers-high, followers-low
  
  // Create vendor state
  const [showCreateVendor, setShowCreateVendor] = useState(false);
  const [newVendor, setNewVendor] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [creatingVendor, setCreatingVendor] = useState(false);
  const [createdVendor, setCreatedVendor] = useState(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState(null);
  const [vendorDetails, setVendorDetails] = useState(null);
  const [loadingVendorDetails, setLoadingVendorDetails] = useState(false);
  const [vendorNotifMetrics, setVendorNotifMetrics] = useState(null);
  
  // Badge management
  const [selectedUserForBadge, setSelectedUserForBadge] = useState(null);
  const [userBadges, setUserBadges] = useState([]);
  const [loadingBadges, setLoadingBadges] = useState(false);
  const [togglingBadge, setTogglingBadge] = useState(null);
  
  // Admin notification state
  const [notificationTarget, setNotificationTarget] = useState("all");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationMetrics, setNotificationMetrics] = useState(null);
  const [individualUserSearch, setIndividualUserSearch] = useState("");
  const [selectedIndividualUser, setSelectedIndividualUser] = useState(null);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateApiKey, setShowCreateApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState({ name: "", description: "" });
  const [creatingApiKey, setCreatingApiKey] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState(null);
  const [copiedApiKey, setCopiedApiKey] = useState(false);
  
  // Email Campaigns state
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignBody, setCampaignBody] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all_opted_in");
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [marketingStats, setMarketingStats] = useState(null);

  // Discounts state
  const [discounts, setDiscounts] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    title: "",
    subtitle: "",
    description: "",
    code: "",
    discount: "",
    icon: "tag",
    color: "#FF6B35",
    merchant: "",
    phone: "",
    email: "",
    website: "",
    active: true,
    order: 0
  });
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, reportsRes, vendorsRes, metricsRes, apiKeysRes, campaignsRes, marketingRes, discountsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/reports`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/notification-metrics`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/api-keys`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/email-campaigns`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketing-stats`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/discounts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data);
      setReports(reportsRes.data);
      setVendors(vendorsRes.data);
      setNotificationMetrics(metricsRes.data);
      setApiKeys(apiKeysRes.data);
      setEmailCampaigns(campaignsRes.data);
      setMarketingStats(marketingRes.data);
      setDiscounts(discountsRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to construct image URLs
  const getImageUrl = (path) => {
    if (!path) return null;
    return `${API}/files/${path}?auth=${token}`;
  };

  const handleDeleteUser = async (userId, username) => {
    if (!confirm(`Are you sure you want to delete @${username}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`User @${username} deleted`);
      setUsers(users.filter(u => u.id !== userId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  const handlePromoteUser = async (userId, username) => {
    try {
      await axios.post(`${API}/admin/users/${userId}/promote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`@${username} is now an admin`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: true } : u));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to promote user");
    }
  };

  const handleDemoteUser = async (userId, username) => {
    if (!confirm(`Remove admin access from @${username}?`)) {
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/demote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`@${username} is no longer an admin`);
      setUsers(users.map(u => u.id === userId ? { ...u, is_admin: false } : u));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to demote user");
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm("Delete this report? This cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/reports/${reportId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Report deleted");
      setReports(reports.filter(r => r.id !== reportId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete report");
    }
  };

  const handleSaveNotes = async (reportId) => {
    setSavingNotes(prev => ({ ...prev, [reportId]: true }));
    try {
      await axios.put(`${API}/admin/reports/${reportId}/notes`, 
        { notes: editingNotes[reportId] || "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notes saved");
      setReports(reports.map(r => r.id === reportId ? { ...r, admin_notes: editingNotes[reportId] } : r));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save notes");
    } finally {
      setSavingNotes(prev => ({ ...prev, [reportId]: false }));
    }
  };

  // Badge management functions
  const openBadgeManager = async (user) => {
    setSelectedUserForBadge(user);
    setLoadingBadges(true);
    try {
      const response = await axios.get(`${API}/admin/users/${user.id}/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserBadges(response.data);
    } catch (e) {
      toast.error("Failed to load badges");
    } finally {
      setLoadingBadges(false);
    }
  };

  const toggleBadge = async (badgeId, isEarned) => {
    if (togglingBadge) return; // Prevent double-clicks
    setTogglingBadge(badgeId);
    try {
      if (isEarned) {
        await axios.delete(`${API}/admin/users/${selectedUserForBadge.id}/badges/${badgeId}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        toast.success("Badge removed");
      } else {
        await axios.post(`${API}/admin/users/${selectedUserForBadge.id}/badges/${badgeId}`, {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        });
        toast.success("Badge added");
      }
      // Refresh badges
      const response = await axios.get(`${API}/admin/users/${selectedUserForBadge.id}/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserBadges(response.data);
    } catch (e) {
      console.error("Badge toggle error:", e);
      const errorMsg = e.response?.data?.detail || e.message || "Failed to update badge";
      toast.error(errorMsg);
    } finally {
      setTogglingBadge(null);
    }
  };

  const handleSendNotification = async () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      toast.error("Please enter both title and message");
      return;
    }
    
    if (notificationTarget === "individual" && !selectedIndividualUser) {
      toast.error("Please select a user");
      return;
    }
    
    setSendingNotification(true);
    try {
      const payload = {
        target: notificationTarget,
        title: notificationTitle,
        message: notificationMessage
      };
      
      // Add user_id for individual notifications
      if (notificationTarget === "individual" && selectedIndividualUser) {
        payload.user_id = selectedIndividualUser.id;
      }
      
      const response = await axios.post(`${API}/admin/send-notification`, payload, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      const recipientText = notificationTarget === "individual" 
        ? selectedIndividualUser.username 
        : `${response.data.recipients} users`;
      toast.success(`Notification sent to ${recipientText}!`);
      setNotificationTitle("");
      setNotificationMessage("");
      setSelectedIndividualUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send notification");
    } finally {
      setSendingNotification(false);
    }
  };

  const isUserAdmin = (u) => u.is_admin || u.is_master_admin;

  // Vendor functions
  const handleCreateVendor = async () => {
    if (!newVendor.name || !newVendor.email || !newVendor.password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setCreatingVendor(true);
    try {
      const response = await axios.post(`${API}/admin/vendors`, newVendor, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Store created vendor info for email template
      setCreatedVendor({
        ...response.data.vendor,
        password: newVendor.password // Keep password for email template
      });
      
      toast.success("Vendor created successfully!");
      setShowCreateVendor(false);
      setNewVendor({ name: "", email: "", password: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create vendor");
    } finally {
      setCreatingVendor(false);
    }
  };

  const handleToggleVendor = async (vendorId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/vendors/${vendorId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? "Vendor disabled" : "Vendor enabled");
      fetchData();
    } catch (e) {
      toast.error("Failed to update vendor status");
    }
  };

  const handleDeleteVendor = async (vendorId, vendorName) => {
    if (!confirm(`Are you sure you want to permanently delete "${vendorName}"? This will also delete all their locations and cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/vendors/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Vendor deleted successfully");
      setExpandedVendor(null);
      setVendorDetails(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const fetchVendorDetails = async (vendorId) => {
    if (expandedVendor === vendorId) {
      setExpandedVendor(null);
      setVendorDetails(null);
      setVendorNotifMetrics(null);
      return;
    }
    
    setExpandedVendor(vendorId);
    setLoadingVendorDetails(true);
    setVendorNotifMetrics(null);
    try {
      const [detailsRes, metricsRes] = await Promise.all([
        axios.get(`${API}/admin/vendors/${vendorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/vendors/${vendorId}/notification-metrics`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setVendorDetails(detailsRes.data);
      setVendorNotifMetrics(metricsRes.data);
    } catch (e) {
      toast.error("Failed to load vendor details");
    } finally {
      setLoadingVendorDetails(false);
    }
  };

  const getLocationUrl = (locationId) => {
    const baseUrl = WEB_URL || window.location.origin;
    return `${baseUrl}/venue/${locationId}`;
  };

  const getVendorLoginUrl = () => {
    // Use the web URL base for the vendor login
    const baseUrl = WEB_URL || window.location.origin;
    return `${baseUrl}/vendor/login`;
  };

  const generateEmailTemplate = () => {
    if (!createdVendor) return "";
    
    const loginUrl = getVendorLoginUrl();
    
    return `Subject: Welcome to PourCircle - Your Vendor Portal Access

Hi ${createdVendor.name} Team,

Welcome to PourCircle! Your vendor account has been created and you can now access your Vendor Portal to manage your venue.

🔗 VENDOR PORTAL LOGIN
${loginUrl}

📧 YOUR LOGIN CREDENTIALS
Email: ${createdVendor.email}
Password: ${createdVendor.password}

🚀 GETTING STARTED
1. Log in to your Vendor Portal using the link above
2. Upload your venue logo
3. Set your default hours and menus
4. Add your locations
5. Link your bartender Stars by searching their username

Once you've set up your page, your customers can find and follow your locations in the PourCircle app!

If you have any questions, please reply to this email.

Cheers,
The PourCircle Team`;
  };

  const copyEmailTemplate = () => {
    const template = generateEmailTemplate();
    navigator.clipboard.writeText(template);
    setCopiedEmail(true);
    toast.success("Email template copied to clipboard!");
    setTimeout(() => setCopiedEmail(false), 3000);
  };

  const filteredUsers = users
    .filter(u => {
      // Text search
      const matchesSearch = 
        u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Role filter
      const matchesRole = roleFilter === "all" || u.role === roleFilter;
      
      // Follower count filter
      const followerCount = u.follower_count ?? u.followers?.length ?? 0;
      let matchesFollowers = true;
      if (followerFilter === "0-24") matchesFollowers = followerCount < 25;
      else if (followerFilter === "25-99") matchesFollowers = followerCount >= 25 && followerCount < 100;
      else if (followerFilter === "100-499") matchesFollowers = followerCount >= 100 && followerCount < 500;
      else if (followerFilter === "500+") matchesFollowers = followerCount >= 500;
      
      return matchesSearch && matchesRole && matchesFollowers;
    })
    .sort((a, b) => {
      const aFollowers = a.follower_count ?? a.followers?.length ?? 0;
      const bFollowers = b.follower_count ?? b.followers?.length ?? 0;
      
      if (sortBy === "followers-high") return bFollowers - aFollowers;
      if (sortBy === "followers-low") return aFollowers - bFollowers;
      // Default: newest first
      return new Date(b.created_at) - new Date(a.created_at);
    });

  // Discount management functions
  const resetDiscountForm = () => {
    setDiscountForm({
      title: "",
      subtitle: "",
      description: "",
      code: "",
      discount: "",
      icon: "tag",
      color: "#FF6B35",
      merchant: "",
      phone: "",
      email: "",
      website: "",
      active: true,
      order: 0
    });
    setEditingDiscount(null);
  };

  const openCreateDiscount = () => {
    resetDiscountForm();
    setShowDiscountModal(true);
  };

  const openEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountForm({
      title: discount.title || "",
      subtitle: discount.subtitle || "",
      description: discount.description || "",
      code: discount.code || "",
      discount: discount.discount || "",
      icon: discount.icon || "tag",
      color: discount.color || "#FF6B35",
      merchant: discount.merchant || "",
      phone: discount.contact?.phone || "",
      email: discount.contact?.email || "",
      website: discount.contact?.website || "",
      active: discount.active !== false,
      order: discount.order || 0
    });
    setShowDiscountModal(true);
  };

  const handleSaveDiscount = async () => {
    if (!discountForm.title || !discountForm.code || !discountForm.merchant) {
      toast.error("Title, code, and merchant are required");
      return;
    }

    setSavingDiscount(true);
    try {
      const payload = {
        title: discountForm.title,
        subtitle: discountForm.subtitle,
        description: discountForm.description,
        code: discountForm.code,
        discount: discountForm.discount,
        icon: discountForm.icon,
        color: discountForm.color,
        merchant: discountForm.merchant,
        contact: {
          phone: discountForm.phone || null,
          email: discountForm.email || null,
          website: discountForm.website || null
        },
        active: discountForm.active,
        order: discountForm.order
      };

      if (editingDiscount) {
        await axios.put(`${API}/admin/discounts/${editingDiscount.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Discount updated");
      } else {
        await axios.post(`${API}/admin/discounts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Discount created");
      }

      setShowDiscountModal(false);
      resetDiscountForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save discount");
    } finally {
      setSavingDiscount(false);
    }
  };

  const handleToggleDiscount = async (discountId) => {
    try {
      const response = await axios.post(`${API}/admin/discounts/${discountId}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.active ? "Discount activated" : "Discount deactivated");
      fetchData();
    } catch (e) {
      toast.error("Failed to toggle discount");
    }
  };

  const handleDeleteDiscount = async (discountId) => {
    if (!window.confirm("Are you sure you want to delete this discount?")) return;
    
    try {
      await axios.delete(`${API}/admin/discounts/${discountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Discount deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete discount");
    }
  };

  const handleUploadPdf = async (discountId, file) => {
    if (!file || file.type !== "application/pdf") {
      toast.error("Please select a PDF file");
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(`${API}/admin/discounts/${discountId}/upload-pdf`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("PDF uploaded successfully");
      fetchData();
      // If we're editing, update the editing discount
      if (editingDiscount && editingDiscount.id === discountId) {
        const updated = await axios.get(`${API}/admin/discounts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const found = updated.data.find(d => d.id === discountId);
        if (found) setEditingDiscount(found);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload PDF");
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleDeletePdf = async (discountId) => {
    if (!window.confirm("Remove the PDF from this discount?")) return;
    
    try {
      await axios.delete(`${API}/admin/discounts/${discountId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("PDF removed");
      fetchData();
      if (editingDiscount && editingDiscount.id === discountId) {
        setEditingDiscount({...editingDiscount, pdf_url: null});
      }
    } catch (e) {
      toast.error("Failed to remove PDF");
    }
  };

  const getDiscountIcon = (iconType) => {
    switch (iconType) {
      case "party": return <PartyPopper className="w-5 h-5" />;
      case "gift": return <Gift className="w-5 h-5" />;
      case "percent": return <Percent className="w-5 h-5" />;
      default: return <Tag className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center justify-between px-4 h-14">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)}
            className="text-white/70 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Admin Dashboard
          </h1>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={fetchData}
            className="text-white/70 hover:text-white"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 py-4 grid grid-cols-3 gap-3">
        <div className="glass-card p-4 text-center">
          <Users className="w-6 h-6 text-primary mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{users.length}</p>
          <p className="text-white/60 text-sm">Users</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Store className="w-6 h-6 text-green-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{vendors.length}</p>
          <p className="text-white/60 text-sm">Vendors</p>
        </div>
        <div className="glass-card p-4 text-center">
          <Flag className="w-6 h-6 text-orange-400 mx-auto mb-2" />
          <p className="text-2xl font-bold text-white">{reports.length}</p>
          <p className="text-white/60 text-sm">Reports</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 mb-4">
        <div className="flex gap-2 overflow-x-auto">
          <Button
            variant={activeTab === "users" ? "default" : "outline"}
            onClick={() => setActiveTab("users")}
            className={activeTab === "users" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
          <Button
            variant={activeTab === "vendors" ? "default" : "outline"}
            onClick={() => setActiveTab("vendors")}
            className={activeTab === "vendors" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Store className="w-4 h-4 mr-2" />
            Vendors
          </Button>
          <Button
            variant={activeTab === "reports" ? "default" : "outline"}
            onClick={() => setActiveTab("reports")}
            className={activeTab === "reports" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Flag className="w-4 h-4 mr-2" />
            Reports
          </Button>
          <Button
            variant={activeTab === "notifications" ? "default" : "outline"}
            onClick={() => setActiveTab("notifications")}
            className={activeTab === "notifications" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Notify
          </Button>
          <Button
            variant={activeTab === "apikeys" ? "default" : "outline"}
            onClick={() => setActiveTab("apikeys")}
            className={activeTab === "apikeys" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </Button>
          <Button
            variant={activeTab === "campaigns" ? "default" : "outline"}
            onClick={() => setActiveTab("campaigns")}
            className={activeTab === "campaigns" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Mail className="w-4 h-4 mr-2" />
            Campaigns
          </Button>
          <Button
            variant={activeTab === "discounts" ? "default" : "outline"}
            onClick={() => setActiveTab("discounts")}
            className={activeTab === "discounts" ? "btn-primary" : "border-white/20 text-white"}
          >
            <Tag className="w-4 h-4 mr-2" />
            Discounts
          </Button>
        </div>
      </div>

      {/* Admin Notifications Tab */}
      {activeTab === "notifications" && (
        <div className="px-4 space-y-4">
          <div className="glass-card p-6">
            <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              Send Notification to Users
            </h2>
            
            {/* Target Selection */}
            <div className="mb-4">
              <Label className="text-white/80 mb-2 block">Target Audience</Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={notificationTarget === "all" ? "default" : "outline"}
                  onClick={() => { setNotificationTarget("all"); setSelectedIndividualUser(null); }}
                  className={notificationTarget === "all" ? "btn-primary" : "border-white/20 text-white"}
                  data-testid="target-all"
                >
                  All Users
                </Button>
                <Button
                  type="button"
                  variant={notificationTarget === "bartenders" ? "default" : "outline"}
                  onClick={() => { setNotificationTarget("bartenders"); setSelectedIndividualUser(null); }}
                  className={notificationTarget === "bartenders" ? "btn-primary" : "border-white/20 text-white"}
                  data-testid="target-bartenders"
                >
                  All Bartenders
                </Button>
                <Button
                  type="button"
                  variant={notificationTarget === "bargoers" ? "default" : "outline"}
                  onClick={() => { setNotificationTarget("bargoers"); setSelectedIndividualUser(null); }}
                  className={notificationTarget === "bargoers" ? "btn-primary" : "border-white/20 text-white"}
                  data-testid="target-bargoers"
                >
                  All Bar-goers
                </Button>
                <Button
                  type="button"
                  variant={notificationTarget === "individual" ? "default" : "outline"}
                  onClick={() => setNotificationTarget("individual")}
                  className={notificationTarget === "individual" ? "btn-primary" : "border-white/20 text-white"}
                  data-testid="target-individual"
                >
                  Individual User
                </Button>
              </div>
            </div>
            
            {/* Individual User Search */}
            {notificationTarget === "individual" && (
              <div className="mb-4">
                <Label className="text-white/80 mb-2 block">Search User</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={individualUserSearch}
                    onChange={(e) => setIndividualUserSearch(e.target.value)}
                    placeholder="Search by username..."
                    className="input-dark pl-10"
                    data-testid="individual-user-search"
                  />
                </div>
                
                {/* User Results */}
                {individualUserSearch && (
                  <div className="mt-2 max-h-48 overflow-y-auto bg-black/50 border border-white/10 rounded-lg">
                    {users
                      .filter(u => u.username?.toLowerCase().includes(individualUserSearch.toLowerCase()))
                      .slice(0, 10)
                      .map(u => (
                        <button
                          key={u.id}
                          onClick={() => {
                            setSelectedIndividualUser(u);
                            setIndividualUserSearch("");
                          }}
                          className="w-full p-3 text-left hover:bg-white/10 flex items-center gap-3 border-b border-white/5 last:border-b-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-bold">
                            {u.name?.charAt(0) || u.username?.charAt(0) || "?"}
                          </div>
                          <div>
                            <p className="text-white text-sm font-medium">{u.name || u.username}</p>
                            <p className="text-white/50 text-xs">@{u.username} • {u.role}</p>
                          </div>
                        </button>
                      ))}
                    {users.filter(u => u.username?.toLowerCase().includes(individualUserSearch.toLowerCase())).length === 0 && (
                      <p className="p-3 text-white/40 text-sm">No users found</p>
                    )}
                  </div>
                )}
                
                {/* Selected User */}
                {selectedIndividualUser && (
                  <div className="mt-2 p-3 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                        {selectedIndividualUser.name?.charAt(0) || selectedIndividualUser.username?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p className="text-white font-medium">{selectedIndividualUser.name || selectedIndividualUser.username}</p>
                        <p className="text-white/60 text-sm">@{selectedIndividualUser.username} • {selectedIndividualUser.role}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedIndividualUser(null)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Title */}
            <div className="mb-4">
              <Label className="text-white/80 mb-2 block">Notification Title</Label>
              <Input
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                placeholder="e.g., Keep your schedule updated!"
                className="input-dark"
                maxLength={100}
                data-testid="notification-title"
              />
            </div>
            
            {/* Message */}
            <div className="mb-4">
              <Label className="text-white/80 mb-2 block">Message</Label>
              <textarea
                value={notificationMessage}
                onChange={(e) => setNotificationMessage(e.target.value)}
                placeholder="e.g., Your followers want to know when you're working. Update your schedule to keep them engaged!"
                className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-primary resize-none"
                rows={4}
                maxLength={500}
                data-testid="notification-message"
              />
              <p className="text-white/40 text-xs mt-1">{notificationMessage.length}/500 characters</p>
            </div>
            
            {/* Send Button */}
            <Button
              onClick={handleSendNotification}
              disabled={sendingNotification || !notificationTitle.trim() || !notificationMessage.trim() || (notificationTarget === "individual" && !selectedIndividualUser)}
              className="btn-primary w-full"
              data-testid="send-notification-btn"
            >
              {sendingNotification ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {notificationTarget === "all" ? "All Users" : 
                           notificationTarget === "bartenders" ? "All Bartenders" : 
                           notificationTarget === "bargoers" ? "All Bar-goers" : 
                           selectedIndividualUser ? `@${selectedIndividualUser.username}` : "Select a User"}
                </>
              )}
            </Button>
          </div>
          
          {/* Quick Templates */}
          <div className="glass-card p-4">
            <h3 className="text-white font-medium mb-3">Quick Templates</h3>
            <div className="space-y-2">
              <button
                onClick={() => {
                  setNotificationTarget("bartenders");
                  setNotificationTitle("Keep your schedule updated!");
                  setNotificationMessage("Your followers want to know when you're working. Update your schedule to keep them engaged and grow your following!");
                }}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <p className="text-white font-medium text-sm">Schedule Reminder</p>
                <p className="text-white/50 text-xs">Remind bartenders to update schedules</p>
              </button>
              <button
                onClick={() => {
                  setNotificationTarget("all");
                  setNotificationTitle("Don't forget about Invites!");
                  setNotificationMessage("Use the invite feature to ask your followers to join you for drinks. It's a great way to connect with friends!");
                }}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <p className="text-white font-medium text-sm">Invite Feature</p>
                <p className="text-white/50 text-xs">Promote the invite feature</p>
              </button>
              <button
                onClick={() => {
                  setNotificationTarget("all");
                  setNotificationTitle("Share your QR code!");
                  setNotificationMessage("Your profile has a unique QR code. Share it with friends so they can follow you instantly!");
                }}
                className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                <p className="text-white font-medium text-sm">QR Code Promotion</p>
                <p className="text-white/50 text-xs">Encourage QR code sharing</p>
              </button>
            </div>
          </div>

          {/* Notification Opt-Out Metrics */}
          {notificationMetrics && (
            <div className="glass-card p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Notification Opt-Out Metrics
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Vendor Notifications */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Bell className="w-4 h-4 text-green-400" />
                    <span className="text-white font-medium text-sm">Vendor Notifications</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white">{notificationMetrics.vendor_notifications?.opt_out_percentage || 0}%</span>
                    <span className="text-white/50 text-xs">opted out</span>
                  </div>
                  <p className="text-white/40 text-xs mt-1">
                    {notificationMetrics.vendor_notifications?.opted_out || 0} of {notificationMetrics.total_users || 0} users
                  </p>
                </div>

                {/* App Tips */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Megaphone className="w-4 h-4 text-blue-400" />
                    <span className="text-white font-medium text-sm">PourCircle App Tips</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white">{notificationMetrics.app_tips?.opt_out_percentage || 0}%</span>
                    <span className="text-white/50 text-xs">opted out</span>
                  </div>
                  <p className="text-white/40 text-xs mt-1">
                    {notificationMetrics.app_tips?.opted_out || 0} of {notificationMetrics.total_users || 0} users
                  </p>
                </div>

                {/* Both Opted Out */}
                <div className="p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <BellOff className="w-4 h-4 text-red-400" />
                    <span className="text-white font-medium text-sm">Both Disabled</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-white">{notificationMetrics.both_opted_out?.percentage || 0}%</span>
                    <span className="text-white/50 text-xs">opted out of all</span>
                  </div>
                  <p className="text-white/40 text-xs mt-1">
                    {notificationMetrics.both_opted_out?.count || 0} users disabled all notifications
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "users" && (
        <>
          {/* Search and Filters */}
          <div className="px-4 mb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/40 focus:outline-none focus:border-primary"
              />
            </div>
            
            {/* Filters Row */}
            <div className="flex gap-2 flex-wrap">
              {/* Role Filter */}
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
              >
                <option value="all">All Roles</option>
                <option value="bartender">Bartenders</option>
                <option value="bargoer">Bar-goers</option>
              </select>
              
              {/* Follower Count Filter */}
              <select
                value={followerFilter}
                onChange={(e) => setFollowerFilter(e.target.value)}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
              >
                <option value="all">All Followers</option>
                <option value="0-24">0-24 followers</option>
                <option value="25-99">25-99 (Rising Star)</option>
                <option value="100-499">100-499 (RockStar)</option>
                <option value="500+">500+ (Legend)</option>
              </select>
              
              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
              >
                <option value="newest">Newest First</option>
                <option value="followers-high">Most Followers</option>
                <option value="followers-low">Least Followers</option>
              </select>
              
              {/* Results count */}
              <span className="px-3 py-2 text-white/40 text-sm">
                {filteredUsers.length} users
              </span>
            </div>
          </div>

          {/* Users List */}
          <div className="px-4 space-y-3">
            {filteredUsers.map((u) => (
              <div key={u.id} className="glass-card p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={getImageUrl(u.profile_image)} />
                      <AvatarFallback className="bg-primary/20 text-primary">
                        {u.name?.charAt(0) || u.username?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium">{u.name || "No name"}</p>
                        {isUserAdmin(u) && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-primary/20 text-primary text-xs rounded-full">
                            <Shield className="w-3 h-3" />
                            {u.is_master_admin ? "Master" : "Admin"}
                          </span>
                        )}
                      </div>
                      <p className="text-white/60 text-sm">@{u.username}</p>
                      <p className="text-white/40 text-xs">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      u.role === "bartender" ? "bg-primary/20 text-primary" : "bg-secondary/20 text-secondary"
                    }`}>
                      {u.role}
                    </span>
                    <div className="flex items-center gap-1">
                      {!u.is_master_admin && (
                        isUserAdmin(u) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDemoteUser(u.id, u.username)}
                            className="text-orange-400 hover:text-orange-300 hover:bg-orange-400/10"
                            title="Remove admin access"
                          >
                            <ShieldOff className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handlePromoteUser(u.id, u.username)}
                            className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                            title="Make admin"
                          >
                            <ShieldCheck className="w-4 h-4" />
                          </Button>
                        )
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteUser(u.id, u.username)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                        title="Delete user"
                        disabled={u.is_master_admin}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-4 text-xs text-white/40">
                  <span>Followers: {u.follower_count ?? u.followers?.length ?? 0}</span>
                  <span>Following: {u.following_count ?? u.following?.length ?? 0}</span>
                  <span>Joined: {new Date(u.created_at).toLocaleDateString()}</span>
                  {u.role === "bartender" && (
                    <button
                      onClick={() => openBadgeManager(u)}
                      className="text-yellow-400 hover:text-yellow-300 flex items-center gap-1"
                    >
                      <Award className="w-3 h-3" />
                      Manage Badges
                    </button>
                  )}
                </div>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-center text-white/60 py-8">No users found</p>
            )}
          </div>
        </>
      )}

      {activeTab === "reports" && (
        <div className="px-4 space-y-3">
          {reports.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <Flag className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/60">No reports yet</p>
            </div>
          ) : (
            reports.map((r) => (
              <div key={r.id} className={`glass-card p-4 ${r.type === 'child_safety' ? 'border-2 border-red-500/50' : ''}`}>
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      r.type === 'child_safety' 
                        ? 'bg-red-500/20 text-red-400' 
                        : 'bg-orange-400/20 text-orange-400'
                    }`}>
                      {r.type === 'child_safety' ? 'CHILD SAFETY' : r.reason}
                    </span>
                    {r.priority === 'high' && (
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">HIGH PRIORITY</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/40 text-xs">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => handleDeleteReport(r.id)}
                      className="text-red-400/60 hover:text-red-400 p-1"
                      title="Delete report"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {r.type === 'child_safety' ? (
                  <>
                    <p className="text-white text-sm">
                      <span className="text-white/60">Reported Username:</span> @{r.reported_username || 'Not specified'}
                    </p>
                    <p className="text-white text-sm">
                      <span className="text-white/60">Reporter Email:</span> {r.reporter_email || 'Not provided'}
                    </p>
                    <p className="text-white/80 text-sm mt-2 p-2 bg-white/5 rounded">{r.description}</p>
                  </>
                ) : (
                  <>
                    <p className="text-white text-sm">
                      <span className="text-white/60">Reporter:</span> @{r.reporter_username}
                    </p>
                    <p className="text-white text-sm">
                      <span className="text-white/60">Reported:</span> @{r.reported_username}
                    </p>
                    {r.details && (
                      <p className="text-white/60 text-sm mt-2">{r.details}</p>
                    )}
                  </>
                )}
                
                {/* Admin Notes */}
                <div className="mt-3 pt-3 border-t border-white/10">
                  <label className="text-white/60 text-xs block mb-1">Admin Notes:</label>
                  <textarea
                    value={editingNotes[r.id] !== undefined ? editingNotes[r.id] : (r.admin_notes || "")}
                    onChange={(e) => setEditingNotes(prev => ({ ...prev, [r.id]: e.target.value }))}
                    placeholder="Document your actions here..."
                    className="w-full p-2 bg-white/5 border border-white/10 rounded text-white text-sm placeholder:text-white/30 resize-none"
                    rows={2}
                  />
                  <Button
                    onClick={() => handleSaveNotes(r.id)}
                    disabled={savingNotes[r.id]}
                    size="sm"
                    className="mt-2 bg-primary/20 text-primary hover:bg-primary/30"
                  >
                    {savingNotes[r.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save Notes"}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === "vendors" && (
        <div className="px-4 space-y-4">
          {/* Create Vendor Button */}
          <Button 
            onClick={() => setShowCreateVendor(true)} 
            className="w-full btn-primary"
            data-testid="create-vendor-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Vendor
          </Button>

          {/* Create Vendor Form */}
          {showCreateVendor && (
            <div className="glass-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-semibold">Create Vendor</h3>
                <button onClick={() => setShowCreateVendor(false)} className="text-white/40 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/80">Venue Name *</Label>
                  <Input
                    value={newVendor.name}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, name: e.target.value }))}
                    className="input-dark"
                    placeholder="The Blue Bar"
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Email *</Label>
                  <Input
                    type="email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor(prev => ({ ...prev, email: e.target.value }))}
                    className="input-dark"
                    placeholder="vendor@example.com"
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white/80">Password *</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={newVendor.password}
                      onChange={(e) => setNewVendor(prev => ({ ...prev, password: e.target.value }))}
                      className="input-dark pr-10"
                      placeholder="Temporary password"
                      data-testid="vendor-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  onClick={handleCreateVendor} 
                  disabled={creatingVendor}
                  className="w-full btn-primary"
                  data-testid="submit-vendor-btn"
                >
                  {creatingVendor ? "Creating..." : "Create Vendor"}
                </Button>
              </div>
            </div>
          )}

          {/* Email Template (shown after creating vendor) */}
          {createdVendor && (
            <div className="glass-card p-6 space-y-4 border-2 border-green-500/30">
              <div className="flex items-center justify-between">
                <h3 className="text-green-400 font-semibold flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Vendor Created! Copy Email Template
                </h3>
                <button onClick={() => setCreatedVendor(null)} className="text-white/40 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="bg-black/40 rounded-lg p-4 max-h-64 overflow-y-auto">
                <pre className="text-white/80 text-sm whitespace-pre-wrap font-mono">
                  {generateEmailTemplate()}
                </pre>
              </div>
              <Button 
                onClick={copyEmailTemplate}
                className={copiedEmail ? "w-full bg-green-500 hover:bg-green-600" : "w-full btn-primary"}
                data-testid="copy-email-btn"
              >
                {copiedEmail ? (
                  <><Check className="w-4 h-4 mr-2" /> Copied!</>
                ) : (
                  <><Copy className="w-4 h-4 mr-2" /> Copy Email Template</>
                )}
              </Button>
            </div>
          )}

          {/* Vendors List */}
          <div className="space-y-3">
            {vendors.map((v) => (
              <div key={v.id} className="glass-card overflow-hidden">
                {/* Vendor Header - Clickable */}
                <button
                  onClick={() => fetchVendorDetails(v.id)}
                  className="w-full p-4 text-left hover:bg-white/5 transition-colors"
                  data-testid={`vendor-expand-${v.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
                        <Store className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{v.name}</p>
                        <p className="text-white/60 text-sm">{v.email}</p>
                        <p className="text-white/40 text-xs">@{v.username}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          v.is_active !== false ? "bg-green-400/20 text-green-400" : "bg-red-400/20 text-red-400"
                        }`}>
                          {v.is_active !== false ? "Active" : "Disabled"}
                        </span>
                        <p className="text-white/40 text-xs mt-1">
                          {v.location_count || 0} location{(v.location_count || 0) !== 1 ? "s" : ""}
                        </p>
                      </div>
                      {expandedVendor === v.id ? (
                        <ChevronUp className="w-5 h-5 text-white/40" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                  </div>
                </button>

                {/* Expanded Vendor Details */}
                {expandedVendor === v.id && (
                  <div className="border-t border-white/10 p-4 bg-white/5">
                    {loadingVendorDetails ? (
                      <div className="flex justify-center py-4">
                        <RefreshCw className="w-5 h-5 text-primary animate-spin" />
                      </div>
                    ) : vendorDetails ? (
                      <div className="space-y-4">
                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/vendor/${v.id}`);
                            }}
                            className="btn-primary"
                          >
                            <Store className="w-4 h-4 mr-1" />
                            Manage Vendor
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleVendor(v.id, v.is_active !== false);
                            }}
                            className={v.is_active !== false 
                              ? "border-red-400/30 text-red-400 hover:bg-red-400/10" 
                              : "border-green-400/30 text-green-400 hover:bg-green-400/10"
                            }
                          >
                            {v.is_active !== false ? "Disable Vendor" : "Enable Vendor"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteVendor(v.id, v.name);
                            }}
                            className="border-red-400/30 text-red-400 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>

                        {/* Locations */}
                        <div>
                          <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                            <Building2 className="w-4 h-4 text-primary" />
                            Locations ({vendorDetails.locations?.length || 0})
                          </h4>
                          {vendorDetails.locations?.length > 0 ? (
                            <div className="space-y-2">
                              {vendorDetails.locations.map((loc) => {
                                const locMetrics = vendorNotifMetrics?.locations?.find(l => l.id === loc.id);
                                return (
                                  <div 
                                    key={loc.id} 
                                    className="p-3 bg-black/30 rounded-lg"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <MapPin className="w-4 h-4 text-primary" />
                                        <div>
                                          <p className="text-white font-medium text-sm">{loc.name}</p>
                                          <p className="text-white/50 text-xs">{loc.address}</p>
                                          <p className="text-white/40 text-xs">{loc.followers?.length || 0} followers</p>
                                        </div>
                                      </div>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(getLocationUrl(loc.id), '_blank');
                                        }}
                                        className="border-white/20 text-white hover:bg-white/10"
                                      >
                                        <ExternalLink className="w-3 h-3 mr-1" />
                                        View Profile
                                      </Button>
                                    </div>
                                    {/* Location Notification Metrics */}
                                    {locMetrics && locMetrics.total_followers > 0 && (
                                      <div className="mt-2 pt-2 border-t border-white/10">
                                        <div className="flex gap-3 text-xs">
                                          <span className="text-green-400">{locMetrics.opted_in} opted in</span>
                                          <span className="text-red-400">{locMetrics.opted_out} opted out ({locMetrics.opt_out_percentage}%)</span>
                                        </div>
                                        <div className="mt-1 h-1.5 bg-red-500/30 rounded-full overflow-hidden">
                                          <div 
                                            className="h-full bg-green-500 rounded-full transition-all"
                                            style={{ width: `${100 - (locMetrics.opt_out_percentage || 0)}%` }}
                                          ></div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-white/40 text-sm">No locations added yet</p>
                          )}
                        </div>

                        {/* Vendor Overall Notification Metrics */}
                        {vendorNotifMetrics && vendorNotifMetrics.master?.total_followers > 0 && (
                          <div className="p-4 bg-primary/10 rounded-lg border border-primary/20">
                            <h4 className="text-white font-medium flex items-center gap-2 mb-3">
                              <BarChart3 className="w-4 h-4 text-primary" />
                              Overall Notification Opt-In/Out
                            </h4>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white/80 text-sm">{vendorNotifMetrics.master.total_followers} total followers</span>
                            </div>
                            <div className="flex gap-4 text-sm">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                <span className="text-white/80">
                                  {vendorNotifMetrics.master.opted_in} opted in ({100 - (vendorNotifMetrics.master.opt_out_percentage || 0)}%)
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <span className="text-white/80">
                                  {vendorNotifMetrics.master.opted_out} opted out ({vendorNotifMetrics.master.opt_out_percentage || 0}%)
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 h-2 bg-red-500/30 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 rounded-full transition-all"
                                style={{ width: `${100 - (vendorNotifMetrics.master.opt_out_percentage || 0)}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
            {vendors.length === 0 && (
              <div className="glass-card p-8 text-center">
                <Store className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">No vendors yet. Create one above!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Badge Manager Dialog */}
      {selectedUserForBadge && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedUserForBadge(null)}>
          <div className="glass-card p-6 w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Manage Badges
              </h3>
              <button onClick={() => setSelectedUserForBadge(null)} className="text-white/60 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-white/60 mb-4">@{selectedUserForBadge.username}</p>
            
            {loadingBadges ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {userBadges.map((badge) => (
                  <div 
                    key={badge.id}
                    className={`p-4 rounded-lg border ${
                      badge.earned 
                        ? 'bg-white/10 border-white/20' 
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${badge.color}20` }}
                        >
                          {badge.icon === 'star' && <Star className="w-5 h-5" style={{ color: badge.color }} />}
                          {badge.icon === 'zap' && <Zap className="w-5 h-5" style={{ color: badge.color }} />}
                          {badge.icon === 'trophy' && <Trophy className="w-5 h-5" style={{ color: badge.color }} />}
                          {badge.icon === 'flag' && <Flag className="w-5 h-5" style={{ color: badge.color }} />}
                          {badge.icon === 'award' && <Award className="w-5 h-5" style={{ color: badge.color }} />}
                          {badge.icon === 'check-circle' && <Check className="w-5 h-5" style={{ color: badge.color }} />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{badge.name}</p>
                          <p className="text-white/50 text-xs">
                            {badge.auto_earned ? 'Auto-earned' : badge.manually_assigned ? 'Admin assigned' : 'Not earned'}
                          </p>
                        </div>
                      </div>
                      {!badge.auto && (
                        <Button
                          onClick={() => toggleBadge(badge.id, badge.earned)}
                          disabled={togglingBadge === badge.id || loadingBadges}
                          size="sm"
                          className={badge.earned 
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30" 
                            : "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          }
                        >
                          {togglingBadge === badge.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : badge.earned ? (
                            "Remove"
                          ) : (
                            "Add"
                          )}
                        </Button>
                      )}
                    </div>
                    <p className="text-white/40 text-xs mt-2">{badge.description}</p>
                    {badge.auto && badge.threshold && (
                      <p className="text-white/30 text-xs mt-1">
                        Requires {badge.threshold}+ followers (current: {selectedUserForBadge.followers?.length || 0})
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* API Keys Tab */}
      {activeTab === "apikeys" && (
        <div className="px-4 space-y-4">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                API Keys
              </h2>
              <Button
                onClick={() => setShowCreateApiKey(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Key
              </Button>
            </div>
            
            <p className="text-white/60 text-sm mb-4">
              Generate API keys for external integrations like ChatGPT plugins.
            </p>

            {/* Create API Key Modal */}
            {showCreateApiKey && (
              <div className="glass-card p-4 mb-4 border border-primary/30">
                <h3 className="text-white font-semibold mb-3">Create New API Key</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white/80">Name *</Label>
                    <Input
                      value={newApiKey.name}
                      onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
                      placeholder="e.g., ChatGPT Plugin"
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Description</Label>
                    <Input
                      value={newApiKey.description}
                      onChange={(e) => setNewApiKey({ ...newApiKey, description: e.target.value })}
                      placeholder="What is this key for?"
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        if (!newApiKey.name) {
                          toast.error("Name is required");
                          return;
                        }
                        setCreatingApiKey(true);
                        try {
                          const res = await axios.post(
                            `${API}/admin/api-keys`,
                            newApiKey,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          setCreatedApiKey(res.data);
                          setApiKeys([...apiKeys, res.data]);
                          toast.success("API key created!");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed to create API key");
                        } finally {
                          setCreatingApiKey(false);
                        }
                      }}
                      disabled={creatingApiKey}
                      className="btn-primary"
                    >
                      {creatingApiKey ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCreateApiKey(false);
                        setNewApiKey({ name: "", description: "" });
                        setCreatedApiKey(null);
                      }}
                      className="border-white/20 text-white"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>

                {/* Show created key */}
                {createdApiKey && (
                  <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-green-400 font-semibold mb-2">API Key Created!</p>
                    <p className="text-white/60 text-sm mb-2">Copy this key now - it won't be shown again:</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-black/30 rounded text-green-400 text-sm break-all">
                        {createdApiKey.key}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(createdApiKey.key);
                          setCopiedApiKey(true);
                          setTimeout(() => setCopiedApiKey(false), 2000);
                        }}
                        className="text-green-400"
                      >
                        {copiedApiKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* API Keys List */}
            <div className="space-y-2">
              {apiKeys.length === 0 ? (
                <p className="text-white/40 text-center py-8">No API keys created yet</p>
              ) : (
                apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className={`p-4 rounded-lg border ${key.is_active ? 'bg-white/5 border-white/10' : 'bg-red-500/5 border-red-500/20'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{key.name}</p>
                        {key.description && <p className="text-white/50 text-sm">{key.description}</p>}
                        <p className="text-white/40 text-xs mt-1">
                          Prefix: {key.key_prefix}*** | Created: {new Date(key.created_at).toLocaleDateString()}
                          {key.last_used && ` | Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            try {
                              const res = await axios.put(
                                `${API}/admin/api-keys/${key.id}/toggle`,
                                {},
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setApiKeys(apiKeys.map(k => k.id === key.id ? { ...k, is_active: res.data.is_active } : k));
                              toast.success(res.data.is_active ? "Key activated" : "Key deactivated");
                            } catch (e) {
                              toast.error("Failed to toggle key");
                            }
                          }}
                          className={key.is_active ? "text-green-400" : "text-red-400"}
                        >
                          {key.is_active ? <ShieldCheck className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={async () => {
                            if (!confirm("Delete this API key? This cannot be undone.")) return;
                            try {
                              await axios.delete(
                                `${API}/admin/api-keys/${key.id}`,
                                { headers: { Authorization: `Bearer ${token}` } }
                              );
                              setApiKeys(apiKeys.filter(k => k.id !== key.id));
                              toast.success("API key deleted");
                            } catch (e) {
                              toast.error("Failed to delete key");
                            }
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Campaigns Tab */}
      {activeTab === "campaigns" && (
        <div className="px-4 space-y-4">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Email Campaigns
              </h2>
              <Button
                onClick={() => setShowCreateCampaign(true)}
                className="btn-primary"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </div>

            {/* Marketing Stats */}
            {marketingStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{marketingStats.opted_in}</p>
                  <p className="text-white/50 text-xs">Opted In</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{marketingStats.opted_out}</p>
                  <p className="text-white/50 text-xs">Opted Out</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{marketingStats.bartenders_opted_in}</p>
                  <p className="text-white/50 text-xs">Bartenders</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{marketingStats.customers_opted_in}</p>
                  <p className="text-white/50 text-xs">Customers</p>
                </div>
              </div>
            )}

            {/* Create Campaign Modal */}
            {showCreateCampaign && (
              <div className="glass-card p-4 mb-4 border border-primary/30">
                <h3 className="text-white font-semibold mb-3">Create Email Campaign</h3>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white/80">Subject *</Label>
                    <Input
                      value={campaignSubject}
                      onChange={(e) => setCampaignSubject(e.target.value)}
                      placeholder="Email subject line"
                      className="bg-white/5 border-white/20 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white/80">Recipients</Label>
                    <div className="flex gap-2 mt-1">
                      {[
                        { value: "all_opted_in", label: "All Opted-In" },
                        { value: "bartenders", label: "Bartenders" },
                        { value: "customers", label: "Customers" }
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          size="sm"
                          variant={campaignFilter === opt.value ? "default" : "outline"}
                          onClick={() => setCampaignFilter(opt.value)}
                          className={campaignFilter === opt.value ? "btn-primary" : "border-white/20 text-white"}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-white/80">Email Body (HTML) *</Label>
                    <textarea
                      value={campaignBody}
                      onChange={(e) => setCampaignBody(e.target.value)}
                      placeholder="<h1>Hello!</h1><p>Your email content here...</p>"
                      rows={8}
                      className="w-full p-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/30 font-mono text-sm"
                    />
                    <p className="text-white/40 text-xs mt-1">
                      Footer with App Store/Play Store badges, address, and unsubscribe link will be added automatically.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        if (!campaignSubject || !campaignBody) {
                          toast.error("Subject and body are required");
                          return;
                        }
                        if (!confirm(`Send this campaign to ${campaignFilter === 'all_opted_in' ? 'all opted-in users' : campaignFilter}?`)) return;
                        
                        setSendingCampaign(true);
                        try {
                          const res = await axios.post(
                            `${API}/admin/email-campaigns`,
                            {
                              subject: campaignSubject,
                              body_html: campaignBody,
                              recipient_filter: campaignFilter
                            },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(`Campaign sent! ${res.data.sent_count} emails delivered.`);
                          setShowCreateCampaign(false);
                          setCampaignSubject("");
                          setCampaignBody("");
                          fetchData(); // Refresh campaigns list
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Failed to send campaign");
                        } finally {
                          setSendingCampaign(false);
                        }
                      }}
                      disabled={sendingCampaign}
                      className="btn-primary"
                    >
                      {sendingCampaign ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Campaign
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowCreateCampaign(false);
                        setCampaignSubject("");
                        setCampaignBody("");
                      }}
                      className="border-white/20 text-white"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Campaigns List */}
            <div className="space-y-2">
              {emailCampaigns.length === 0 ? (
                <p className="text-white/40 text-center py-8">No campaigns sent yet</p>
              ) : (
                emailCampaigns.map((campaign) => (
                  <div
                    key={campaign.id}
                    className="p-4 bg-white/5 rounded-lg border border-white/10"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-white font-medium">{campaign.subject}</p>
                        <p className="text-white/50 text-sm mt-1">
                          To: {campaign.recipient_filter} | 
                          Sent: {campaign.sent_count}/{campaign.total_recipients} |
                          Failed: {campaign.failed_count}
                        </p>
                        <p className="text-white/40 text-xs mt-1">
                          {campaign.sent_at ? new Date(campaign.sent_at).toLocaleString() : 'Not sent'}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        campaign.status === 'sent' ? 'bg-green-500/20 text-green-400' :
                        campaign.status === 'partial' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {campaign.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Discounts Tab */}
      {activeTab === "discounts" && (
        <div className="px-4 space-y-4">
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Bartender Discounts
              </h2>
              <Button onClick={openCreateDiscount} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Add Discount
              </Button>
            </div>

            <p className="text-white/60 text-sm mb-6">
              Manage exclusive discount offers shown to bartenders on their Discounts page.
            </p>

            {/* Discounts List */}
            {discounts.length === 0 ? (
              <div className="text-center py-8">
                <Tag className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No discounts yet</p>
                <p className="text-white/40 text-sm">Create your first discount offer for bartenders</p>
              </div>
            ) : (
              <div className="space-y-3">
                {discounts.map((discount) => (
                  <div 
                    key={discount.id}
                    className={`p-4 rounded-lg border ${discount.active ? 'bg-white/5 border-white/10' : 'bg-white/2 border-white/5 opacity-60'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${discount.color}20` }}
                      >
                        <span style={{ color: discount.color }}>
                          {getDiscountIcon(discount.icon)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-white font-medium">{discount.title}</p>
                            <p className="text-primary text-sm">{discount.subtitle}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleToggleDiscount(discount.id)}
                              className="text-white/60 hover:text-white h-8 w-8"
                              title={discount.active ? "Deactivate" : "Activate"}
                            >
                              {discount.active ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5" />}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditDiscount(discount)}
                              className="text-white/60 hover:text-white h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteDiscount(discount.id)}
                              className="text-white/60 hover:text-red-400 h-8 w-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-sm text-white/50">
                          <span className="bg-white/10 px-2 py-0.5 rounded">{discount.merchant}</span>
                          <span>Code: <span className="font-mono text-white/70">{discount.code}</span></span>
                          <span>{discount.discount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Discount Create/Edit Modal */}
      {showDiscountModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingDiscount ? "Edit Discount" : "Create Discount"}
              </h3>
              <Button variant="ghost" size="icon" onClick={() => setShowDiscountModal(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            <div className="p-4 space-y-4">
              {/* Title */}
              <div>
                <Label className="text-white/80">Title *</Label>
                <Input
                  value={discountForm.title}
                  onChange={(e) => setDiscountForm({...discountForm, title: e.target.value})}
                  placeholder="Mobile Bars for Your Parties"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>

              {/* Subtitle */}
              <div>
                <Label className="text-white/80">Subtitle</Label>
                <Input
                  value={discountForm.subtitle}
                  onChange={(e) => setDiscountForm({...discountForm, subtitle: e.target.value})}
                  placeholder="10% off with code POURCIRCLE"
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-white/80">Description</Label>
                <textarea
                  value={discountForm.description}
                  onChange={(e) => setDiscountForm({...discountForm, description: e.target.value})}
                  placeholder="Full description of the offer..."
                  className="w-full bg-white/5 border border-white/10 text-white rounded-md p-3 mt-1 min-h-[80px]"
                />
              </div>

              {/* Merchant & Code */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Merchant *</Label>
                  <Input
                    value={discountForm.merchant}
                    onChange={(e) => setDiscountForm({...discountForm, merchant: e.target.value})}
                    placeholder="Company Name"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Discount Code *</Label>
                  <Input
                    value={discountForm.code}
                    onChange={(e) => setDiscountForm({...discountForm, code: e.target.value.toUpperCase()})}
                    placeholder="POURCIRCLE"
                    className="bg-white/5 border-white/10 text-white mt-1 font-mono"
                  />
                </div>
              </div>

              {/* Discount Amount & Icon */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white/80">Discount Amount</Label>
                  <Input
                    value={discountForm.discount}
                    onChange={(e) => setDiscountForm({...discountForm, discount: e.target.value})}
                    placeholder="10% or $20 off"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white/80">Icon</Label>
                  <div className="flex gap-2 mt-1">
                    {["tag", "party", "gift", "percent"].map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setDiscountForm({...discountForm, icon})}
                        className={`p-2 rounded-lg border ${discountForm.icon === icon ? 'border-primary bg-primary/20' : 'border-white/10 bg-white/5'}`}
                      >
                        {getDiscountIcon(icon)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Color */}
              <div>
                <Label className="text-white/80">Brand Color</Label>
                <div className="flex gap-2 mt-1">
                  {["#FF6B35", "#9333EA", "#059669", "#3B82F6", "#EAB308", "#EC4899"].map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setDiscountForm({...discountForm, color})}
                      className={`w-8 h-8 rounded-full border-2 ${discountForm.color === color ? 'border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                  <input
                    type="color"
                    value={discountForm.color}
                    onChange={(e) => setDiscountForm({...discountForm, color: e.target.value})}
                    className="w-8 h-8 rounded cursor-pointer"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="border-t border-white/10 pt-4">
                <Label className="text-white/80 mb-2 block">Contact Information</Label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-white/40" />
                    <Input
                      value={discountForm.phone}
                      onChange={(e) => setDiscountForm({...discountForm, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-white/40" />
                    <Input
                      value={discountForm.email}
                      onChange={(e) => setDiscountForm({...discountForm, email: e.target.value})}
                      placeholder="contact@merchant.com"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-white/40" />
                    <Input
                      value={discountForm.website}
                      onChange={(e) => setDiscountForm({...discountForm, website: e.target.value})}
                      placeholder="www.merchant.com"
                      className="bg-white/5 border-white/10 text-white"
                    />
                  </div>
                </div>
              </div>

              {/* PDF Upload - Only show when editing an existing discount */}
              {editingDiscount && (
                <div className="border-t border-white/10 pt-4">
                  <Label className="text-white/80 mb-2 block">PDF Details Document</Label>
                  <p className="text-white/50 text-xs mb-3">
                    Upload a PDF with full details that bartenders can view when they tap the discount.
                  </p>
                  
                  {editingDiscount.pdf_url ? (
                    <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <FileText className="w-5 h-5 text-green-400" />
                      <div className="flex-1">
                        <p className="text-white text-sm">PDF uploaded</p>
                        <a 
                          href={`${API}/files/${editingDiscount.pdf_url}?auth=${token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary text-xs hover:underline"
                        >
                          View PDF
                        </a>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePdf(editingDiscount.id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        id="pdf-upload"
                        className="hidden"
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            handleUploadPdf(editingDiscount.id, e.target.files[0]);
                          }
                        }}
                      />
                      <label 
                        htmlFor="pdf-upload"
                        className={`flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/20 rounded-lg cursor-pointer hover:bg-white/10 transition ${uploadingPdf ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        {uploadingPdf ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-white text-sm">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <FileText className="w-4 h-4 text-white/60" />
                            <span className="text-white text-sm">Choose PDF</span>
                          </>
                        )}
                      </label>
                      <span className="text-white/40 text-xs">Max 10MB</span>
                    </div>
                  )}
                </div>
              )}

              {!editingDiscount && (
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white/50 text-sm">
                    <FileText className="w-4 h-4 inline mr-1" />
                    Save the discount first, then edit it to upload a PDF.
                  </p>
                </div>
              )}

              {/* Order */}
              <div>
                <Label className="text-white/80">Display Order</Label>
                <Input
                  type="number"
                  value={discountForm.order}
                  onChange={(e) => setDiscountForm({...discountForm, order: parseInt(e.target.value) || 0})}
                  className="bg-white/5 border-white/10 text-white mt-1 w-24"
                />
                <p className="text-white/40 text-xs mt-1">Lower numbers appear first</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1 border-white/20 text-white"
                  onClick={() => setShowDiscountModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 btn-primary"
                  onClick={handleSaveDiscount}
                  disabled={savingDiscount}
                >
                  {savingDiscount ? "Saving..." : (editingDiscount ? "Update" : "Create")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
